import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { RedirectFormFields, RedirectFormInputs, RedirectMatchModeEnum, RedirectsQueryKeys, useCreateRedirectsMutation } from '@/modules/Sites';

import { DomainVisitors } from '@/modules/Domains';
import { DEFAULT_REDIRECT_STATUS_CODE, RedirectEndpointEnum, RedirectMatchMode } from '@/modules/Sites';

import { RedirectsFormView } from './RedirectsFormView';
import { redirectFormValidationSchema } from './validation';

interface Props {
  siteId: string;
}

export const RedirectsCreateFormContainer: FC<Props> = memo(({ siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<RedirectFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof RedirectFormInputs>>(null);

  const { mutate: createRedirectMutation, isLoading: createRedirectsIsLoading } =
  useCreateRedirectsMutation(siteId)({
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.refetchQueries([RedirectsQueryKeys.GetAll, siteId]),
        queryClient.refetchQueries([RedirectsQueryKeys.Conflicts, siteId]),
      ]);

      appMessageApi.success('Редірект успішно створено!');

      navigate(AppRoutes.RedirectsById.replace(':siteId', siteId).replace(':redirectId', data.id));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof RedirectFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: RedirectFormFields = {
    siteId,
    forVisitor: DomainVisitors.Any,
    sourceType: RedirectEndpointEnum.Node,
    sourceNodeId: undefined,
    sourcePath: undefined,
    targetType: RedirectEndpointEnum.Path,
    targetNodeId: undefined,
    targetPath: undefined,
    statusCode: DEFAULT_REDIRECT_STATUS_CODE,
    matchMode: RedirectMatchModeEnum.Exact,
    isActive: true,
  };

  const handleFinish = useCallback((values: RedirectFormFields): void => {
    setFormErrors(null);
    createRedirectMutation(values);
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.Redirects.replace(':siteId', siteId));
  }, [siteId]);

  return (
    <RedirectsFormView
      formName="redirect-create"
      formType="create"
      form={ form }
      formErrors={ formErrors }
      initialValues={ initialValues }
      isFormLoading={ createRedirectsIsLoading }
      validationSchema={ redirectFormValidationSchema }
      handleBack={ handleBack }
      handleFinish={ handleFinish }
      handleFinishFailed={ handleFinishFailed }
    />
  );
});
